import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { closeSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import { useIntercom } from "react-use-intercom";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import OTPField from "../../Components/Form Fields/otp-field";
import PrimaryButton from "../../Components/Buttons/primary-button";
import ErrorPage from "../../Components/Others/error-page";

import renderAlertConfirmationModal from "../../Organisms/Modals/alert-confirmation-modal";

import {
  useGetPartnerCustomerInformation,
  useImagePreload,
  useIsCustomerFromPartnerOnboarded,
  useOnPartnerOnboardingLoad,
} from "../../CustomHooks/Queries";
import {
  useLoginWith2FACode,
  useRequestLogin2FACode,
  useSavePartnerCustomerInformation,
} from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";

import {
  dispatchAPIErrorToast,
  dispatchErrorToast,
  dispatchSuccessToast,
  LocalStorage,
  onSignOut,
  transformCountryCodeFormatToUSNumber,
} from "../../Utils/helpers";
import { APIQueryKeys, appUtilityKeys, debugProps } from "../../Utils/constants";

import { ArrowLeftCircle, ChevronRight, Phone, SecuredPersonalData } from "../../Assets/Icons";
import { syncInstantReleaseFromSearch } from "../InstantRelease/instant-data";

const PartnershipHeader = ({ partnerLogoUrl }) => {
  return (
    <header>
      <img className="logo" src={partnerLogoUrl} alt="" />
    </header>
  );
};

export default function PartnerSignUp() {
  const resendOtpRef = useRef(false);
  const hasAutoSavedPartnerInfoRef = useRef(false);

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);

  const { show: showIntercom } = useIntercom();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const modalContext = useModalContext();

  const { onModalClose } = modalContext;

  const { onUserCredentialsUpdate } = useAppUtilityContext();

  const formProps = useForm();

  const partner_id = useMemo(() => new URLSearchParams(window.location.search).get("partnerId"), []);
  const partner_customer_id = useMemo(() => new URLSearchParams(window.location.search).get("customerReferenceId"), []);

  useEffect(() => {
    syncInstantReleaseFromSearch(new URLSearchParams(window.location.search));
  }, []);

  const hasRequiredParams = !!partner_id && !!partner_customer_id;

  const {
    isLoading: isLoadingPartnerOnboarding,
    data: partnerOnboarding,
    error: partnerOnboardingError,
    isError: isPartnerOnboardingError,
  } = useOnPartnerOnboardingLoad(partner_id, {
    enabled: !!partner_id,
  });

  const {
    isFetching: isCheckingIsCustomerFromPartnerOnboarded,
    data: customerFromPartnerOnboarded,
    error: customerFromPartnerOnboardedError,
    isError: isCustomerFromPartnerOnboardedError,
  } = useIsCustomerFromPartnerOnboarded({ partner_id, partner_customer_id }, { enabled: hasRequiredParams });

  const {
    isLoading: isGettingPartnerCustomerInformation,
    data: partnerCustomerInformation,
    error: partnerCustomerInformationError,
    isError: isPartnerCustomerInformationError,
  } = useGetPartnerCustomerInformation({ partner_id, partner_customer_id }, { enabled: hasRequiredParams });

  const { isLoading: isLoadingPartnershipLogo, data: partnerLogo } = useImagePreload(
    [partnerOnboarding?.data?.data?.partner_logo_url],
    {
      enabled: !!partnerOnboarding?.data?.data?.partner_logo_url,
    }
  );

  useEffect(() => {
    const isCustomerOnboarded = customerFromPartnerOnboarded?.data?.data?.is_customer_onboarded;
    setAgreedToTerms(isCustomerOnboarded);
  }, [customerFromPartnerOnboarded]);

  useEffect(() => {
    const agentId = new URLSearchParams(window.location.search).get("agentId");
    if (!!agentId) {
      LocalStorage.write(appUtilityKeys.agentId, agentId);
    }
  }, []);

  const {
    handleSubmit,
    getValues,
    formState: { isValid },
    resetField,
  } = formProps;

  const formattedUserName = useMemo(() => {
    return transformCountryCodeFormatToUSNumber(customerFromPartnerOnboarded?.data?.data?.phone_number ?? "");
  }, [customerFromPartnerOnboarded]);

  const { mutate: SavePartnerInfo } = useSavePartnerCustomerInformation({
    onSuccess: () => {
      setAgreedToTerms(true);
      queryClient.refetchQueries(APIQueryKeys.isCustomerFromPartnerOnboarded);
      queryClient.refetchQueries(APIQueryKeys.getPartnerCustomerInformation);
    },
    onError: (err) => {
      if (err?.response?.status === 404) {
        renderAlertConfirmationModal(modalContext, {
          title: "Update Required",
          description: err?.response?.data?.error,
          CTAButtonText: "Got it",
          hideSecondaryCTAButton: true,
          onCTAButtonClick: () => onModalClose(),
        });
      } else {
        dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "savePartnerInfo" });
      }
    },
  });

  useEffect(() => {
    const customerData = customerFromPartnerOnboarded?.data?.data;
    if (!customerData || customerData.is_customer_onboarded || hasAutoSavedPartnerInfoRef.current) {
      return;
    }
    hasAutoSavedPartnerInfoRef.current = true;
    SavePartnerInfo({
      partner_id,
      partner_customer_id,
      agent_id: LocalStorage.read(appUtilityKeys.agentId),
    });
  }, [customerFromPartnerOnboarded, SavePartnerInfo, partner_id, partner_customer_id]);

  const { isLoading: isRequestingLogin2FACode, mutate: RequestLogin2FACode } = useRequestLogin2FACode({
    onSuccess: () => {
      setShowOtpVerification(true);
      if (resendOtpRef.current) {
        resendOtpRef.current = false;
        dispatchSuccessToast(`New OTP code was sent at ${formattedUserName}`, { key: "request2FA" });
      } else {
        resetField("otp");
      }
    },
    onError: () => {
      dispatchErrorToast("Oops! Something went wrong!", { key: "request2FA" });
    },
    onSettled: () => (resendOtpRef.current = false),
  });

  const { isLoading: isLoggingInWith2FACode, mutate: LoginWith2FACode } = useLoginWith2FACode({
    onSuccess: (data) => {
      onSignOut({ redirectOnLogout: false });
      queryClient.resetQueries();
      onUserCredentialsUpdate(data);
      dispatchSuccessToast("Verification success");
      navigate("/login");
    },
    onError: (err) =>
      dispatchErrorToast(err?.response?.data?.error?.msg, {
        key: "loginWith2FA",
      }),
  });

  const onSubmit = () => {
    closeSnackbar();

    const payload = {
      ...debugProps,
      phoneNumber: customerFromPartnerOnboarded?.data?.data?.phone_number,
    };

    if (!showOtpVerification || resendOtpRef.current) {
      !isSignInLoading && RequestLogin2FACode(payload);
    } else {
      payload.phoneNumberCode = getValues("otp");
      !isSignInLoading && LoginWith2FACode(payload);
    }
  };

  const onFormError = (errors) => {
    dispatchErrorToast(errors.otp.message, { key: "otp" });
  };

  const handleGoToOTPVerification = () => {
    onSubmit();
  };

  const handleGoBackToLoginPreference = () => {
    setShowOtpVerification(false);
  };

  const isLoadingPageContent =
    isCheckingIsCustomerFromPartnerOnboarded ||
    isGettingPartnerCustomerInformation ||
    isLoadingPartnerOnboarding ||
    isLoadingPartnershipLogo;

  const showIdentityStep = agreedToTerms;

  const isErrorPageContent =
    isCustomerFromPartnerOnboardedError ||
    isPartnerCustomerInformationError ||
    isPartnerOnboardingError ||
    !hasRequiredParams;

  const errorMessage =
    (isCustomerFromPartnerOnboardedError
      ? customerFromPartnerOnboardedError?.response?.data?.error
      : isPartnerCustomerInformationError
      ? partnerCustomerInformationError?.response?.data?.error
      : isPartnerOnboardingError
      ? partnerOnboardingError?.response?.data?.error
      : "Link Invalid") ?? "Network Error";

  const isSignInLoading = isRequestingLogin2FACode || isLoggingInWith2FACode;

  return isErrorPageContent ? (
    <ErrorPage
      isPageNotFound={customerFromPartnerOnboardedError?.response?.status === 404}
      errorMessage={errorMessage}
    />
  ) : (
    <div
      className={`onboarding-page partner-signup justify-content-${
        isLoadingPageContent ? "center" : "sm-center justify-content-start"
      }`}
    >
      {isLoadingPageContent ? (
        <CircularProgress sx={{ color: "var(--mist)" }} />
      ) : (
        <>
          {!showIdentityStep && <CircularProgress sx={{ color: "var(--mist)" }} />}
          {showIdentityStep && !showOtpVerification && (
            <div className="max-width-container justify-content-center confirm-identity-view">
              <div className="ci-partner-header">
                <img
                  className="ci-partner-logo"
                  src={partnerLogo?.[0]}
                  alt=""
                />
              </div>
              <div className="ci-identity-card">
                <SecuredPersonalData className="ci-icon" />
                <div className="ci-title">Log in to Stake</div>
                <div className="ci-subtitle">We'll send a verification code to your phone.</div>
              </div>
              <div className="ci-options">
                <button className="ci-option" onClick={handleGoToOTPVerification}>
                  <span className="ci-option-icon">
                    <Phone />
                  </span>
                  <div className="ci-option-info">
                    <div className="ci-option-label">Phone Number</div>
                    <div className="ci-option-value">
                      Text to {customerFromPartnerOnboarded?.data?.data?.masked_phone_number}
                    </div>
                  </div>
                  {isSignInLoading ? (
                    <CircularProgress className="ci-loading" />
                  ) : (
                    <ChevronRight className="ci-chevron" />
                  )}
                </button>
              </div>
              <button className="ci-support" onClick={() => showIntercom()}>
                Need help? Contact Support
              </button>
            </div>
          )}
          {showIdentityStep && showOtpVerification && (
            <>
              <FormProvider {...formProps}>
                <div className="otp-card">
                  <form className="otp-form" onSubmit={handleSubmit(onSubmit, onFormError)} autoComplete="off">
                    <div className="otp-header">
                      <button className="back-button" onClick={handleGoBackToLoginPreference} type="button" aria-label="Go back">
                        <ArrowLeftCircle />
                      </button>
                      <h1 className="otp-title">Phone Number Verification</h1>
                      <p className="otp-subtitle">
                        Enter the 6-digit verification code sent to{" "}
                        <span className="username-badge">{formattedUserName}</span>
                      </p>
                    </div>
                    <div className="otp-field">
                      <OTPField />
                      <button
                        className="resend-otp-button"
                        onClick={() => {
                          resendOtpRef.current = true;
                          onSubmit();
                        }}
                        type="button"
                      >
                        Didn’t receive a text? Resend code
                      </button>
                    </div>
                    <PrimaryButton className="w-100" type="submit" disabled={!isValid || isSignInLoading}>
                      Next {isSignInLoading && <CircularProgress />}
                    </PrimaryButton>
                  </form>
                </div>
              </FormProvider>
              <button className="help-button" onClick={() => showIntercom()}>
                Trouble logging in? Contact Support
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
